import { useCallback, useState } from 'react';
import { useIsMobileView } from '../../components/ResponsiveSwitcher';

const STORAGE_KEY = '@Arnold/newReportMenuExpanded';

const readStoredState = (): boolean | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? null : stored === 'true';
  } catch {
    return null;
  }
};

const writeStoredState = (expanded: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) — the menu still works, it just won't remember.
  }
};

/**
 * Expanded/collapsed state of the report menu, persisted in local storage.
 * Until the user makes a choice, the default follows the viewport: expanded on desktop, collapsed on mobile.
 */
export const useReportMenuExpanded = () => {
  const isMobile = useIsMobileView();
  const [storedExpanded, setStoredExpanded] = useState<boolean | null>(readStoredState);

  const expanded = storedExpanded ?? !isMobile;

  const toggleExpanded = useCallback(() => {
    setStoredExpanded((current) => {
      const next = !(current ?? !isMobile);
      writeStoredState(next);
      return next;
    });
  }, [isMobile]);

  return { expanded, toggleExpanded };
};
